// src/shared/components/BugsViewer.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAppSelector } from '@app/store/hooks';
import Modal from '@shared/components/Modal';
import Input from '@shared/components/Input';
import Textarea from '@shared/components/Textarea';
import {
  type BugReport,
  type BugStatus,
  getBugReports,
  createBugReport,
  updateBugReport,
  updateBugReportStatus,
  replyToBugReport,
  updateBugReply,
  deleteBugReply,
  cleanupFixedBugReports,
  deleteBugReport,
} from '@shared/services/bugReportService';

const statusLabels: Record<BugStatus, string> = {
  not_fixed: 'برطرف نشده',
  in_fixing: 'در حال رفع',
  fixed: 'رفع شده',
  wont_fix: 'رفع نخواهد شد',
};

const statusColors: Record<BugStatus, string> = {
  not_fixed: 'bg-red-100 text-red-800 border-red-200',
  in_fixing: 'bg-amber-100 text-amber-800 border-amber-200',
  fixed: 'bg-green-100 text-green-800 border-green-200',
  wont_fix: 'bg-gray-100 text-gray-600 border-gray-200',
};

function isRouteNotFound(error: any): boolean {
  const msg = String(error?.response?.data?.message ?? error?.message ?? '');
  return error?.response?.status === 404 || /could not be found|route.*not found/i.test(msg);
}

interface BugsViewerProps {
  logsEnabled?: boolean;
  featureRequestsEnabled?: boolean;
}

function formatPersianDate(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BugsViewer: React.FC<BugsViewerProps> = ({ logsEnabled = false, featureRequestsEnabled = false }) => {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const currentUserId = useAppSelector((s) => s.user.user?.id);
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isListOpen, setIsListOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [editBug, setEditBug] = useState<BugReport | null>(null);
  const [statusFilter, setStatusFilter] = useState<BugStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | number | null>(null);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);
  const [deleteConfirmBug, setDeleteConfirmBug] = useState<BugReport | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [replyingId, setReplyingId] = useState<string | number | null>(null);
  const [editingReplyId, setEditingReplyId] = useState<string | number | null>(null);
  const [editingReplyBody, setEditingReplyBody] = useState('');
  const [isCleanupConfirmOpen, setIsCleanupConfirmOpen] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const desktopLeftClass = logsEnabled ? 'sm:left-24' : 'sm:left-4';

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setBugs([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await getBugReports();
      setBugs(list);
      setHasLoaded(true);
    } catch (e: any) {
      if (isRouteNotFound(e)) {
        setBugs([]);
        setError(null);
      } else {
        setError(e?.message || 'خطا در بارگذاری گزارش‌ها');
        setBugs([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentUserId]);

  useEffect(() => {
    if (isListOpen) refresh();
  }, [isListOpen, refresh]);

  useEffect(() => {
    setBugs([]);
    setHasLoaded(false);
    setError(null);
  }, [isAuthenticated, currentUserId]);

  const addBug = async (report: { pageName: string; title: string; description: string; steps?: string; url?: string }) => {
    try {
      await createBugReport({
        page_name: report.pageName,
        title: report.title,
        description: report.description,
        steps: report.steps,
        url: report.url,
      });
      toast.success('باگ ثبت شد');
      setIsReportOpen(false);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || 'خطا در ثبت باگ');
    }
  };

  const updateBug = async (id: string | number, report: { pageName: string; title: string; description: string; steps?: string; url?: string }) => {
    try {
      await updateBugReport(id, {
        page_name: report.pageName,
        title: report.title,
        description: report.description,
        steps: report.steps,
        url: report.url,
      });
      toast.success('باگ به‌روزرسانی شد');
      setEditBug(null);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || 'خطا در به‌روزرسانی باگ');
    }
  };

  const updateStatus = async (id: string | number, status: BugStatus) => {
    try {
      await updateBugReportStatus(id, status);
      setBugs((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch (e: any) {
      toast.error(e?.message || 'خطا در به‌روزرسانی وضعیت');
    }
  };

  const sendReply = async (id: string | number) => {
    const body = (replyDrafts[String(id)] ?? '').trim();
    if (!body) return;
    setReplyingId(id);
    try {
      const updated = await replyToBugReport(id, body);
      setBugs((prev) => prev.map((bug) => (bug.id === id ? updated : bug)));
      setReplyDrafts((prev) => ({ ...prev, [String(id)]: '' }));
      toast.success('پاسخ ثبت شد');
    } catch (e: any) {
      toast.error(e?.message || 'خطا در ثبت پاسخ');
    } finally {
      setReplyingId(null);
    }
  };

  const saveReply = async (bugId: string | number, replyId: string | number) => {
    const body = editingReplyBody.trim();
    if (!body) return;
    setReplyingId(replyId);
    try {
      const updated = await updateBugReply(bugId, replyId, body);
      setBugs((prev) => prev.map((bug) => (bug.id === bugId ? updated : bug)));
      setEditingReplyId(null);
      setEditingReplyBody('');
      toast.success('پاسخ ویرایش شد');
    } catch (e: any) {
      toast.error(e?.message || 'خطا در ویرایش پاسخ');
    } finally {
      setReplyingId(null);
    }
  };

  const removeReply = async (bugId: string | number, replyId: string | number) => {
    setReplyingId(replyId);
    try {
      const updated = await deleteBugReply(bugId, replyId);
      setBugs((prev) => prev.map((bug) => (bug.id === bugId ? updated : bug)));
      toast.success('پاسخ حذف شد');
    } catch (e: any) {
      toast.error(e?.message || 'خطا در حذف پاسخ');
    } finally {
      setReplyingId(null);
    }
  };

  const cleanupFixed = async () => {
    setCleaningUp(true);
    try {
      const count = await cleanupFixedBugReports(30);
      toast.success(count > 0 ? `${count} باگ قدیمی پاک شد` : 'باگ رفع‌شدهٔ قدیمی برای پاک‌سازی وجود نداشت');
      setIsCleanupConfirmOpen(false);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || 'خطا در پاک‌سازی باگ‌ها');
    } finally {
      setCleaningUp(false);
    }
  };

  const openDeleteConfirm = (bug: BugReport) => setDeleteConfirmBug(bug);

  const removeBug = async (id: string | number) => {
    setDeleteConfirmBug(null);
    setDeletingId(id);
    try {
      await deleteBugReport(id);
      setBugs((prev) => prev.filter((b) => b.id !== id));
      toast.success('گزارش حذف شد');
    } catch (e: any) {
      toast.error(e?.message || 'خطا در حذف');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredBugs = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('fa-IR');
    const statusOrder: Record<BugStatus, number> = { not_fixed: 0, in_fixing: 1, fixed: 2, wont_fix: 3 };
    return bugs
      .filter((bug) => {
        const matchesStatus = statusFilter === 'all' || bug.status === statusFilter;
        const searchable = `${bug.title} ${bug.description} ${bug.pageName} ${bug.submittedBy.name}`.toLocaleLowerCase('fa-IR');
        return matchesStatus && (!normalizedQuery || searchable.includes(normalizedQuery));
      })
      .sort((a, b) => {
        const statusDifference = statusOrder[a.status] - statusOrder[b.status];
        if (statusDifference !== 0) return statusDifference;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
  }, [bugs, query, statusFilter]);

  if (!isListOpen) {
    return (
      <button
        type="button"
        onClick={() => {
          setIsListOpen(true);
        }}
        className={`fixed left-3 right-3 z-40 flex h-12 items-center justify-center gap-2 rounded-2xl border border-white/20 bg-gradient-to-l from-blue-600 via-blue-700 to-indigo-700 px-4 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(37,99,235,0.28)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(37,99,235,0.34)] disabled:cursor-wait disabled:opacity-80 ${
          featureRequestsEnabled ? 'bottom-[4.5rem]' : 'bottom-3'
        } ${desktopLeftClass} sm:bottom-4 sm:right-auto sm:w-auto sm:min-w-[148px]`}
        title="گزارش باگ‌ها"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 4.5h.008v.008H12V16.5z" />
        </svg>
        <span>گزارش باگ</span>
        {(loading || hasLoaded) && <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{loading ? '…' : bugs.length}</span>}
      </button>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[40] flex items-end justify-center bg-slate-950/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
        onClick={() => setIsListOpen(false)}
      >
        <motion.section
          initial={{ opacity: 0, y: 32, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', damping: 26, stiffness: 260 }}
          onClick={(event) => event.stopPropagation()}
          dir="rtl"
          className="flex h-[100dvh] w-full max-w-[1680px] flex-col overflow-hidden bg-slate-50 shadow-2xl sm:h-[96vh] sm:rounded-xl sm:border sm:border-slate-200"
          aria-label="مرکز گزارش باگ"
        >
          <header className="relative shrink-0 border-b-2 border-amber-500 bg-white px-4 py-3 text-slate-900 sm:px-6">
            <div className="relative flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-700 sm:h-11 sm:w-11">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 4.5h.008v.008H12V16.5z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="truncate text-base font-bold sm:text-lg">گزارش‌های باگ</h2>
                  <p className="mt-0.5 text-xs text-slate-500">مشاهده، پیگیری و ثبت مشکلات سامانه</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsListOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
                aria-label="بستن"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </header>

          <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 sm:px-6 sm:py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="relative min-w-0 flex-1">
                <span className="sr-only">جست‌وجوی گزارش‌ها</span>
                <svg className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m21 21-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="جست‌وجو در عنوان، توضیحات، صفحه یا ثبت‌کننده…"
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pr-10 pl-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:bg-white focus:ring-4 focus:ring-amber-100"
                />
              </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as BugStatus | 'all')}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-100 sm:w-40"
            >
                <option value="all">همه وضعیت‌ها</option>
              {(Object.keys(statusLabels) as BugStatus[]).map((s) => (
                <option key={s} value={s}>
                  {statusLabels[s]}
                </option>
              ))}
            </select>
            <button
                type="button"
              onClick={() => setIsReportOpen(true)}
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 sm:min-w-[152px]"
            >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                ثبت باگ جدید
            </button>
            <button
              type="button"
              onClick={() => setIsCleanupConfirmOpen(true)}
              className="flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 sm:min-w-[190px]"
            >
              پاک‌سازی رفع‌شده‌های قدیمی
            </button>
          </div>
          </div>

          <main className="min-h-0 flex-1 overflow-y-auto px-3 py-2 sm:px-6 sm:py-3">
          {!isAuthenticated && (
              <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">
                برای مشاهده و ثبت باگ وارد شوید.
              </div>
          )}
          {isAuthenticated && error && (
              <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 text-center text-sm text-red-700">
                <span>{error}</span>
                <button type="button" onClick={refresh} className="rounded-xl bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700">تلاش دوباره</button>
              </div>
          )}
            {isAuthenticated && !error && loading && (
              <div className="space-y-2">
                {[0, 1, 2, 3].map((item) => (
                  <div key={item} className="h-20 animate-pulse rounded-lg border border-slate-200 bg-white p-3">
                    <div className="h-5 w-24 rounded bg-slate-200" />
                    <div className="mt-5 h-5 w-2/3 rounded bg-slate-200" />
                    <div className="mt-3 h-4 w-full rounded bg-slate-100" />
                    <div className="mt-2 h-4 w-4/5 rounded bg-slate-100" />
                  </div>
                ))}
              </div>
            )}
            {isAuthenticated && !error && !loading && filteredBugs.length === 0 && (
              <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-4 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M9.75 9.75h.008v.008H9.75V9.75zm4.5 0h.008v.008h-.008V9.75zM9.5 15.25a3.5 3.5 0 015 0M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="mt-3 font-semibold text-slate-800">گزارشی پیدا نشد</h3>
                <p className="mt-1 text-sm text-slate-500">فیلتر یا عبارت جست‌وجو را تغییر دهید.</p>
              </div>
            )}
            {isAuthenticated && !error && !loading && filteredBugs.length > 0 && (
              <div className="space-y-2">
                <div className="hidden grid-cols-[25rem_minmax(10rem,1fr)_15rem_19rem] items-center gap-x-3 rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-right text-[11px] font-semibold text-slate-500 xl:grid">
                  <div>وضعیت و عنوان</div>
                  <div>توضیحات</div>
                  <div>ثبت‌کننده و صفحه</div>
                  <div className="text-left">عملیات</div>
                </div>
                {filteredBugs.map((bug) => {
                  const isExpanded = expandedId === bug.id;
                  return (
                    <article key={bug.id} className="group flex flex-col rounded-md border border-slate-200 bg-white p-2.5 text-right shadow-sm transition hover:border-amber-300 hover:bg-amber-50/30 xl:grid xl:grid-cols-[25rem_minmax(10rem,1fr)_15rem_19rem] xl:items-center xl:gap-x-3 xl:px-3 xl:py-2">
                      <div className="flex min-w-0 items-center justify-between gap-3 xl:col-start-1 xl:row-start-1">
                        <div className="min-w-0 xl:flex xl:items-center xl:gap-2">
                          {bug.status === 'not_fixed' && (
                            <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.14)]" title="این باگ هنوز رفع نشده است" aria-label="رفع نشده" />
                          )}
                          <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusColors[bug.status]}`}>{statusLabels[bug.status]}</span>
                          {bug.replies.length > 0 && (
                            <span className="inline-flex shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                              پاسخ داده شده
                            </span>
                          )}
                          <h3 className="mt-1 break-words text-sm font-bold leading-6 text-slate-900 xl:mt-0 xl:truncate xl:text-base">{bug.title || '(بدون عنوان)'}</h3>
                        </div>
                        <span className="shrink-0 whitespace-nowrap text-xs text-slate-400">{formatPersianDate(bug.createdAt)}</span>
                      </div>

                      <p className={`mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-slate-600 xl:col-start-2 xl:row-start-1 xl:mt-0 ${isExpanded ? '' : 'max-h-5 overflow-hidden xl:min-w-0 xl:truncate'}`}>{bug.description}</p>

                      <div className="mt-2 flex min-w-0 flex-wrap gap-1.5 text-xs text-slate-500 xl:col-start-3 xl:row-start-1 xl:mt-0 xl:flex-nowrap">
                        <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.12a7.5 7.5 0 0115 0A17.93 17.93 0 0112 21.75c-2.68 0-5.22-.59-7.5-1.63z" /></svg>
                          {bug.submittedBy.name}
                        </span>
                        <span className="inline-flex min-w-0 flex-1 items-center gap-1 whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-1 font-mono" title={bug.pageName}>
                          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17.25 6.75L21 10.5l-3.75 3.75M6.75 17.25L3 13.5l3.75-3.75M14.25 4.5l-4.5 15" /></svg>
                          <span className="max-w-[220px] truncate">{bug.pageName || 'صفحه نامشخص'}</span>
                        </span>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm xl:col-span-4 xl:row-start-2">
                          {bug.steps && (
                            <div>
                              <p className="mb-2 font-semibold text-slate-700">مراحل تکرار</p>
                              <ol className="space-y-1.5 text-slate-600">
                                {bug.steps.split(/\n/).filter(Boolean).map((step, index) => <li key={`${bug.id}-${index}`}>{index + 1}. {step}</li>)}
                              </ol>
                            </div>
                          )}
                          {bug.url && /^https?:\/\//i.test(bug.url) && (
                            <a href={bug.url} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800">
                              <span className="truncate">باز کردن آدرس ثبت‌شده</span>
                              <span aria-hidden="true">↗</span>
                            </a>
                          )}
                          <div className="space-y-2 border-t border-slate-200 pt-3">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-semibold text-slate-700">پاسخ‌ها ({bug.replies.length})</p>
                              <span className="text-xs text-slate-400">همه کاربران می‌توانند پاسخ دهند</span>
                            </div>
                            {bug.replies.length > 0 && (
                              <div className="space-y-2">
                                {bug.replies.map((reply) => (
                                  <div key={reply.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                    <div className="flex items-center justify-between gap-2 text-xs">
                                      <span className="font-semibold text-slate-700">{reply.authorName}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-slate-400">{formatPersianDate(reply.updatedAt || reply.createdAt)}</span>
                                        {String(reply.userId) === String(currentUserId) && editingReplyId !== reply.id && (
                                          <>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEditingReplyId(reply.id);
                                                setEditingReplyBody(reply.body);
                                              }}
                                              className="font-medium text-amber-700 hover:text-amber-800"
                                            >
                                              ویرایش
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => removeReply(bug.id, reply.id)}
                                              disabled={replyingId === reply.id}
                                              className="font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                                            >
                                              حذف
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {editingReplyId === reply.id && String(reply.userId) === String(currentUserId) ? (
                                      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
                                        <textarea
                                          rows={2}
                                          value={editingReplyBody}
                                          onChange={(event) => setEditingReplyBody(event.target.value)}
                                          maxLength={5000}
                                          className="min-h-16 flex-1 resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
                                        />
                                        <div className="flex gap-2">
                                          <button
                                            type="button"
                                            onClick={() => saveReply(bug.id, reply.id)}
                                            disabled={replyingId === reply.id || !editingReplyBody.trim()}
                                            className="h-10 rounded-lg bg-amber-600 px-3 text-sm font-semibold text-white disabled:opacity-50"
                                          >
                                            {replyingId === reply.id ? 'در حال ثبت…' : 'ذخیره'}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setEditingReplyId(null);
                                              setEditingReplyBody('');
                                            }}
                                            className="h-10 rounded-lg border border-slate-200 px-3 text-sm text-slate-600"
                                          >
                                            انصراف
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-slate-600">{reply.body}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                              <textarea
                                rows={2}
                                value={replyDrafts[String(bug.id)] ?? ''}
                                onChange={(event) => setReplyDrafts((prev) => ({ ...prev, [String(bug.id)]: event.target.value }))}
                                placeholder="پاسخ خود را بنویسید…"
                                className="min-h-16 flex-1 resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
                              />
                              <button
                                type="button"
                                onClick={() => sendReply(bug.id)}
                                disabled={replyingId === bug.id || !(replyDrafts[String(bug.id)] ?? '').trim()}
                                className="h-10 rounded-lg bg-amber-600 px-4 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {replyingId === bug.id ? 'در حال ارسال…' : 'ارسال پاسخ'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-2 flex flex-col gap-2 border-t border-slate-100 pt-2 xl:col-start-4 xl:row-start-1 xl:mt-0 xl:w-auto xl:flex-row xl:items-center xl:justify-between xl:border-t-0 xl:pt-0">
                        <button type="button" onClick={() => setExpandedId(isExpanded ? null : bug.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-amber-700" aria-expanded={isExpanded}>
                          {isExpanded ? 'بستن جزئیات' : 'مشاهده جزئیات'}
                          <svg className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                        </button>
                        <div className="flex items-center gap-2">
                          <select value={bug.status} onChange={(event) => updateStatus(bug.id, event.target.value as BugStatus)} className="h-8 min-w-0 flex-1 rounded border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none focus:border-amber-400 sm:flex-none">
                            {(Object.keys(statusLabels) as BugStatus[]).map((status) => <option key={status} value={status}>{statusLabels[status]}</option>)}
                          </select>
                          {bug.isOwner && (
                            <button type="button" onClick={() => setEditBug(bug)} className="h-8 rounded border border-slate-200 px-2.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50">ویرایش</button>
                          )}
                          {(bug.isOwner || bug.status === 'fixed') && (
                            <button type="button" onClick={() => openDeleteConfirm(bug)} disabled={deletingId === bug.id} className="h-8 rounded border border-red-200 px-2.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50">{deletingId === bug.id ? '…' : 'حذف'}</button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
          )}
          </main>
        </motion.section>
      </motion.div>

      <ReportBugModal
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        onSubmit={addBug}
      />
      <ReportBugModal
        isOpen={editBug !== null}
        onClose={() => setEditBug(null)}
        onSubmit={(data) => editBug && updateBug(editBug.id, data)}
        initialBug={editBug ?? undefined}
        title="ویرایش گزارش باگ"
      />

      <Modal
        isOpen={!!deleteConfirmBug}
        onClose={() => setDeleteConfirmBug(null)}
        title="تأیید حذف"
        size="sm"
        showCloseButton={true}
        footer={
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setDeleteConfirmBug(null)}
              className="text-sm px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => deleteConfirmBug && removeBug(deleteConfirmBug.id)}
              disabled={deletingId === deleteConfirmBug?.id}
              className="text-sm px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deletingId === deleteConfirmBug?.id ? 'در حال حذف...' : 'حذف'}
            </button>
          </div>
        }
      >
        {deleteConfirmBug && (
          <p className="text-gray-600">
            آیا از حذف گزارش باگ «{deleteConfirmBug.title || '(بدون عنوان)'}» اطمینان دارید؟
          </p>
        )}
      </Modal>

      <Modal
        isOpen={isCleanupConfirmOpen}
        onClose={() => setIsCleanupConfirmOpen(false)}
        title="پاک‌سازی باگ‌های قدیمی"
        size="sm"
        showCloseButton={true}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsCleanupConfirmOpen(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={cleanupFixed}
              disabled={cleaningUp}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
            >
              {cleaningUp ? 'در حال پاک‌سازی...' : 'پاک‌سازی'}
            </button>
          </div>
        }
      >
        <p className="text-sm leading-7 text-gray-600">
          فقط باگ‌های رفع‌شدهٔ خودتان که حداقل ۳۰ روز از ثبت آن‌ها گذشته باشد پاک می‌شوند. باگ‌های فعال و گزارش‌های دیگر کاربران باقی می‌مانند.
        </p>
      </Modal>
    </>
  );
};

interface ReportBugModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (report: {
    pageName: string;
    title: string;
    description: string;
    steps?: string;
    url?: string;
  }) => void;
  /** When set, modal is in edit mode: prefilled and uses title prop. */
  initialBug?: BugReport | null;
  title?: string;
}

const PAGE_NAME_MAX = 500;
const TITLE_MAX = 500;
const DESCRIPTION_MIN = 3;
const DESCRIPTION_MAX = 5000;

const ReportBugModal: React.FC<ReportBugModalProps> = ({ isOpen, onClose, onSubmit, initialBug, title: titleProp }) => {
  const [pageName, setPageName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<string[]>(['']);
  const [errors, setErrors] = useState<{ pageName?: string; title?: string; description?: string }>({});

  useEffect(() => {
    if (isOpen) {
      if (initialBug) {
        setPageName(initialBug.pageName || '');
        setTitle(initialBug.title || '');
        setDescription(initialBug.description || '');
        setSteps(
          initialBug.steps && initialBug.steps.trim()
            ? initialBug.steps.trim().split(/\n/).filter(Boolean)
            : ['']
        );
      } else {
        setPageName(window.location.pathname || '');
        setTitle('');
        setDescription('');
        setSteps(['']);
      }
      setErrors({});
    }
  }, [isOpen, initialBug]);

  const setStepAt = (index: number, value: string) => {
    setSteps((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const addStep = () => setSteps((prev) => [...prev, '']);
  const removeStep = (index: number) => {
    if (steps.length <= 1) return;
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const next: typeof errors = {};
    const desc = description.trim();
    if (!desc) {
      next.description = 'توضیحات باگ الزامی است.';
    } else if (desc.length < DESCRIPTION_MIN) {
      next.description = `حداقل ${DESCRIPTION_MIN} کاراکتر وارد کنید.`;
    } else if (desc.length > DESCRIPTION_MAX) {
      next.description = `حداکثر ${DESCRIPTION_MAX} کاراکتر مجاز است.`;
    }
    const page = pageName.trim();
    if (page.length > PAGE_NAME_MAX) {
      next.pageName = `حداکثر ${PAGE_NAME_MAX} کاراکتر مجاز است.`;
    }
    const t = title.trim();
    if (t.length > TITLE_MAX) {
      next.title = `حداکثر ${TITLE_MAX} کاراکتر مجاز است.`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const stepList = steps.map((s) => s.trim()).filter(Boolean);
    onSubmit({
      pageName: pageName.trim() || window.location.pathname || 'نامشخص',
      title: title.trim() || '(بدون عنوان)',
      description: description.trim(),
      steps: stepList.length > 0 ? stepList.join('\n') : undefined,
      url: window.location.href,
    });
  };

  const clearError = (field: keyof typeof errors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={titleProp ?? 'گزارش باگ جدید'}
      size="lg"
      footer={
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 sm:w-auto"
          >
            انصراف
          </button>
          <button
            type="submit"
            form={initialBug ? 'edit-bug-form' : 'report-bug-form'}
            className="w-full rounded-xl bg-gradient-to-l from-amber-500 to-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md sm:w-auto"
          >
            ذخیره
          </button>
        </div>
      }
    >
      <form id={initialBug ? 'edit-bug-form' : 'report-bug-form'} onSubmit={handleSubmit} noValidate className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
          <Input
            label="صفحه (اختیاری)"
            value={pageName}
            onChange={(e) => { setPageName(e.target.value); clearError('pageName'); }}
            placeholder="/path/to/page"
            className="text-sm"
            error={errors.pageName}
            helperText={!errors.pageName ? 'مسیر صفحه در اپ؛ در صورت خالی بودن، مسیر فعلی ذخیره می‌شود.' : undefined}
          />
          </div>
          <div>
          <Input
            label="عنوان (اختیاری)"
            value={title}
            onChange={(e) => { setTitle(e.target.value); clearError('title'); }}
            placeholder="خلاصه باگ"
            className="text-sm"
            error={errors.title}
            helperText={!errors.title ? `حداکثر ${TITLE_MAX} کاراکتر.` : undefined}
          />
          </div>
        </div>
        <div>
          <Textarea
            label="توضیحات"
            value={description}
            onChange={(e) => { setDescription(e.target.value); clearError('description'); }}
            placeholder="شرح باگ و رفتار مشاهده‌شده را بنویسید"
            rows={4}
            className="text-sm"
            required
            error={errors.description}
            helperText={!errors.description ? `الزامی؛ حداقل ${DESCRIPTION_MIN} و حداکثر ${DESCRIPTION_MAX} کاراکتر.` : undefined}
          />
        </div>
        <div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
          <label className="mb-1 block text-sm font-semibold text-slate-700">مراحل تکرار (اختیاری)</label>
          <p className="mb-3 text-xs text-slate-500">هر مرحله را جداگانه وارد کنید تا بازسازی مشکل ساده‌تر باشد.</p>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={index} className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-xs font-semibold text-slate-500 shadow-sm">{index + 1}</span>
                <Input
                  value={step}
                  onChange={(e) => setStepAt(index, e.target.value)}
                  placeholder={index === 0 ? 'مثلاً: به صفحه لاگین برو' : `مرحله ${index + 1}`}
                  className="text-sm flex-1"
                />
                {steps.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeStep(index)}
                    className="shrink-0 rounded-lg border border-red-200 bg-white px-2.5 py-2 text-xs text-red-600 transition hover:bg-red-50"
                  >
                    حذف
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addStep}
              className="w-full rounded-xl border border-dashed border-amber-400 bg-white px-3 py-2 text-sm font-medium text-amber-700 transition hover:bg-amber-50 sm:w-auto"
            >
              + افزودن مرحله
            </button>
          </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default BugsViewer;
export type { BugReport, BugStatus } from '@shared/services/bugReportService';
